import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import caregiverService from '../../services/caregiverService';

// Async thunks
export const createCaregiver = createAsyncThunk(
  'caregiver/createCaregiver',
  async ({ practiceId, caregiverData }, { rejectWithValue }) => {
    try {
      const response = await caregiverService.createCaregiver(practiceId, caregiverData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create caregiver');
    }
  }
);

export const fetchCaregiver = createAsyncThunk(
  'caregiver/fetchCaregiver',
  async ({ practiceId, caregiverId }, { rejectWithValue }) => {
    try {
      const response = await caregiverService.getCaregiver(practiceId, caregiverId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch caregiver');
    }
  }
);

export const fetchCaregivers = createAsyncThunk(
  'caregiver/fetchCaregivers',
  async ({ practiceId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await caregiverService.getCaregivers(practiceId, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch caregivers');
    }
  }
);

export const updateCaregiver = createAsyncThunk(
  'caregiver/updateCaregiver',
  async ({ practiceId, caregiverId, caregiverData }, { rejectWithValue }) => {
    try {
      const response = await caregiverService.updateCaregiver(practiceId, caregiverId, caregiverData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update caregiver');
    }
  }
);

export const deleteCaregiver = createAsyncThunk(
  'caregiver/deleteCaregiver',
  async ({ practiceId, caregiverId }, { rejectWithValue }) => {
    try {
      const response = await caregiverService.deleteCaregiver(practiceId, caregiverId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete caregiver');
    }
  }
);

const initialState = {
  caregivers: [],
  currentCaregiver: null,
  isLoading: false,
  error: null,
};

const caregiverSlice = createSlice({
  name: 'caregiver',
  initialState,
  reducers: {
    setCaregivers: (state, action) => {
      state.caregivers = action.payload;
    },
    setCurrentCaregiver: (state, action) => {
      state.currentCaregiver = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create caregiver
      .addCase(createCaregiver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCaregiver.fulfilled, (state, action) => {
        state.isLoading = false;
        state.caregivers.push(action.payload.data);
      })
      .addCase(createCaregiver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch caregiver
      .addCase(fetchCaregiver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCaregiver.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCaregiver = action.payload.data;
      })
      .addCase(fetchCaregiver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch caregivers
      .addCase(fetchCaregivers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCaregivers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.caregivers = action.payload.data || [];
      })
      .addCase(fetchCaregivers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update caregiver
      .addCase(updateCaregiver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCaregiver.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle both response formats: with data property or without
        const updatedCaregiver = action.payload?.data || action.payload;
        if (updatedCaregiver && updatedCaregiver.id) {
          const index = state.caregivers.findIndex(c => c.id === updatedCaregiver.id);
          if (index !== -1) {
            state.caregivers[index] = updatedCaregiver;
          } else {
            // If not found in list, add it (in case it was restored from deleted state)
            state.caregivers.push(updatedCaregiver);
          }
        }
      })
      .addCase(updateCaregiver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete caregiver
      .addCase(deleteCaregiver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCaregiver.fulfilled, (state, action) => {
        state.isLoading = false;
        // Remove the deleted caregiver from the list
        state.caregivers = state.caregivers.filter(c => c.id !== action.meta.arg.caregiverId);
      })
      .addCase(deleteCaregiver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setCaregivers,
  setCurrentCaregiver,
  setLoading,
  setError,
  clearError,
} = caregiverSlice.actions;

export default caregiverSlice.reducer;
